import React, { useState, useEffect } from 'react';
import { Job, Crew, JobStatus, JobType } from '../types';
import { BrandName, Logo } from './Logo';
import UAVMissionModule from './UAVMissionModule';

type DocType = 'Estimate' | 'Work Order' | 'Invoice';

interface Props {
  job: Job;
  crew?: Crew;
  onClose: () => void;
}

const WorkOrderModal: React.FC<Props> = ({ job, crew, onClose }) => {
  const [docType, setDocType] = useState<DocType>('Work Order');

  useEffect(() => {
    if (job.status === JobStatus.LEAD || job.status === JobStatus.ESTIMATING) {
      setDocType('Estimate');
    } else if (job.status === JobStatus.COMPLETED || job.status === JobStatus.INVOICED || job.status === JobStatus.PAID) {
      setDocType('Invoice');
    } else {
      setDocType('Work Order');
    }
  }, [job.status]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[120] p-4 print:p-0 print:bg-white print:block">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl print:shadow-none print:max-h-none print:rounded-none">
        
        {/* Header Actions - Hidden when printing */}
        <div className="sticky top-0 bg-white border-b border-stone-100 p-4 flex justify-between items-center print:hidden z-10">
          <div className="flex space-x-2 bg-stone-100 p-1 rounded-xl">
            {(['Estimate', 'Work Order', 'Invoice'] as DocType[]).map(type => (
              <button
                key={type}
                onClick={() => setDocType(type)}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition ${docType === type ? 'bg-white text-bark shadow-sm' : 'text-stone-500 hover:text-bark'}`}
              >
                {type}
              </button>
            ))}
          </div>
          <div className="flex space-x-3">
            <button 
              onClick={onClose}
              className="px-4 py-2 text-stone-500 font-bold hover:bg-stone-50 rounded-xl transition"
            >
              Close
            </button>
            <button 
              onClick={handlePrint}
              className="px-6 py-2 bg-palm text-white font-bold rounded-xl hover:bg-gecko transition flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
              Print / Save PDF
            </button>
          </div>
        </div>

        {/* Printable Content */}
        <div className="p-8 md:p-12 print:p-0">
          
          {/* Company Header */}
          <div className="flex justify-between items-start border-b-2 border-bark pb-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <Logo className="w-12 h-12" />
                <BrandName className="text-4xl" />
              </div>
              <div className="mt-4 text-sm text-stone-500 space-y-1">
                <p>4314 Fay Dr</p>
                <p>Columbus, GA 31907 (Chattahoochee Valley)</p>
                <p>706-326-7034</p>
                <p>mrgreen@greentreearboriculture.com</p>
              </div>
            </div>
            <div className="text-right">
              <h2 className="text-3xl font-bold text-stone-300 uppercase tracking-wider mb-2">{docType}</h2>
              <p className="text-sm font-bold text-stone-600">
                {docType === 'Estimate' ? 'EST' : docType === 'Invoice' ? 'INV' : 'WO'} #: <span className="font-mono">{job.id.toUpperCase()}</span>
              </p>
              <p className="text-sm text-stone-500 mt-1">Date: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          {/* Job Details Grid */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div className="bg-stone-50 p-6 rounded-2xl print:bg-transparent print:p-0 print:border print:border-stone-200 print:p-4">
              <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">Client Information</h3>
              <p className="font-bold text-lg text-bark">{job.clientName}</p>
              <p className="text-stone-600 mt-1">{job.address || 'No Data'}</p>
              <div className="mt-4 space-y-2 text-sm">
                <p><span className="font-bold text-stone-500 mr-2">Phone:</span> {job.phone || 'No Data'}</p>
                <p><span className="font-bold text-stone-500 mr-2">Email:</span> {job.email || 'No Data'}</p>
              </div>
            </div>

            <div className="bg-stone-50 p-6 rounded-2xl print:bg-transparent print:p-0 print:border print:border-stone-200 print:p-4">
              <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">Job Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-stone-200 pb-2">
                  <span className="font-bold text-stone-500">Service Type</span>
                  <span className="font-bold text-bark">{job.serviceType}</span>
                </div>
                <div className="flex justify-between border-b border-stone-200 pb-2">
                  <span className="font-bold text-stone-500">Status</span>
                  <span className="font-bold text-bark">{job.status}</span>
                </div>
                <div className="flex justify-between border-b border-stone-200 pb-2">
                  <span className="font-bold text-stone-500">Scheduled</span>
                  <span className="font-bold text-bark">
                    {job.scheduledDate ? new Date(job.scheduledDate).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute:'2-digit'}) : 'TBD'}
                  </span>
                </div>
                <div className="flex justify-between pb-2">
                  <span className="font-bold text-stone-500">Assigned Crew</span>
                  <span className="font-bold text-bark">{crew ? crew.name : 'Unassigned'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tree Inventory */}
          {job.inventory && job.inventory.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-bark border-b border-stone-200 pb-2 mb-4">Tree Inventory & Scope</h3>
              <table className="w-full text-left text-sm">
                <thead className="bg-stone-50 print:bg-stone-100">
                  <tr>
                    <th className="p-3 font-bold text-stone-600">Species</th>
                    <th className="p-3 font-bold text-stone-600">DBH</th>
                    <th className="p-3 font-bold text-stone-600">Health</th>
                    <th className="p-3 font-bold text-stone-600">Location</th>
                    <th className="p-3 font-bold text-stone-600">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {job.inventory.map(tree => (
                    <tr key={tree.id}>
                      <td className="p-3 font-medium text-bark">{tree.species}</td>
                      <td className="p-3 text-stone-600">{tree.diameter}"</td>
                      <td className="p-3 text-stone-600">{tree.health}</td>
                      <td className="p-3 text-stone-600">{tree.location}</td>
                      <td className="p-3 text-stone-500 text-xs italic">{tree.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Attachments & Diagrams */}
          {job.attachments && job.attachments.length > 0 && (
            <div className="mb-8 print:break-before-page">
              <h3 className="text-lg font-bold text-bark border-b border-stone-200 pb-2 mb-4">Attachments & Diagrams</h3>
              <div className="grid grid-cols-2 gap-4">
                {job.attachments.map((src, idx) => (
                  <div key={idx} className="rounded-xl overflow-hidden border border-stone-200">
                    <img src={src} alt={`Attachment ${idx + 1}`} className="w-full h-auto object-contain" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Line Items */}
          {job.lineItems && job.lineItems.length > 0 && (
            <div className="mb-12">
              <h3 className="text-lg font-bold text-bark border-b border-stone-200 pb-2 mb-4">Services & Pricing</h3>
              <table className="w-full text-left text-sm">
                <thead className="bg-stone-50 print:bg-stone-100">
                  <tr>
                    <th className="p-3 font-bold text-stone-600">Description</th>
                    <th className="p-3 font-bold text-stone-600 text-right">Qty</th>
                    <th className="p-3 font-bold text-stone-600 text-right">Unit Price</th>
                    <th className="p-3 font-bold text-stone-600 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {job.lineItems.map(item => (
                    <tr key={item.id}>
                      <td className="p-3 font-medium text-bark">{item.description}</td>
                      <td className="p-3 text-stone-600 text-right">{item.quantity}</td>
                      <td className="p-3 text-stone-600 text-right">${(Number(item.unitPrice) || 0).toFixed(2)}</td>
                      <td className="p-3 font-bold text-bark text-right">${((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="p-4 text-right font-bold text-stone-500 uppercase tracking-wider">Total Amount</td>
                    <td className="p-4 text-right font-black text-xl text-palm">${(Number(job.totalPrice) || 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* UAV Mission Module */}
          {job.type === JobType.DRONE_MISSION && docType === 'Work Order' && (
            <div className="mb-12">
              <UAVMissionModule mission={job.missionDetails} />
            </div>
          )}

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-12 mt-16 pt-8 border-t border-stone-200">
            {docType === 'Invoice' ? (
              <div className="col-span-2 text-center">
                <p className="text-lg font-bold text-bark mb-2">Thank you for your business!</p>
                <p className="text-sm text-stone-500">Payment is due within 30 days of completion. Please make checks payable to Greentree Arboriculture.</p>
              </div>
            ) : (
              <>
                <div>
                  <div className="border-b border-stone-300 h-10 mb-2"></div>
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider">Client Signature</p>
                  <p className="text-xs text-stone-400 mt-1">
                    {docType === 'Estimate' ? 'I accept this estimate and authorize the work.' : 'I authorize the work described above.'}
                  </p>
                </div>
                <div>
                  <div className="border-b border-stone-300 h-10 mb-2"></div>
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider">Date</p>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default WorkOrderModal;